import type { ReactNode } from "react";

import type { BusbarSelectionResponse, UnverifiedValue } from "../services/busbarSelection";
import type { CheckKey } from "../services/busbarSelectionContract";
import { EMPTY_QUANTITY, formatQuantity } from "../utils/formatQuantity";
import { BUSBAR_MATERIAL_LABELS, CASE_LABELS, FAULT_TYPE_LABELS } from "./busbarSelectionDraft";
import {
  arrangementText,
  CHECK_LABELS,
  checkResultKey,
  checkWord,
  DATA_NOT_VERIFIED,
  unverifiedTitle,
} from "./busbarSelectionResultWords";
import { statusHeading } from "./BusbarSelectionResultSummary";
import { StatusIcon } from "./StatusIcon";
import { checkTone } from "./statusTone";
import { SOURCE_LABELS } from "./StarterCoordinationResultSummary";

interface BusbarSelectionResultPanelProps {
  result: BusbarSelectionResponse;
}

// The values of one check that are not verified, listed under its message.
function NotVerified({ values }: { values: readonly UnverifiedValue[] }) {
  if (values.length === 0) {
    return null;
  }
  return (
    <p data-not-verified={values.length}>
      {values.map((value) => (
        <span key={value.path} data-unverified-path={value.path} title={unverifiedTitle(value)}>
          {DATA_NOT_VERIFIED}: {value.label}
        </span>
      ))}
    </p>
  );
}

export const DISCLAIMER =
  "Calculation results are engineering design checks, not a statutory compliance " +
  "declaration. Confirm the governing project basis, the linked fault study, every busbar " +
  "rating and the builder's design verification before approval or issue.";

// One cell holding an engineering figure: four significant figures with the unit on screen,
// the exact value in the tooltip (display rule A12); an em dash for none.
function Quantity({ value, unit }: { value: string | null; unit: string }) {
  const quantity = formatQuantity(value, unit);
  return <td title={quantity.exact ?? undefined}>{quantity.display}</td>;
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section aria-label={title}>
      <h3>{title}</h3>
      <div data-table-scroll>{children}</div>
    </section>
  );
}

type Figure = { name: string; value: string | null; unit: string };

// The figures the response holds for a check, and only those: nothing is calculated here. A
// check the response carries no figure for (the temperature rise, the neutral bar, the
// clearances) shows an empty quantity and the engine's reason beside it.
function figuresOf(result: BusbarSelectionResponse, key: CheckKey): Figure[] {
  switch (key) {
    case "screening":
      return result.system_kind === "BUSWAY"
        ? result.busway_selections.map((selection) => ({
            name: `${BUSBAR_MATERIAL_LABELS[selection.material]} utilisation`,
            value: selection.utilisation_percent,
            unit: "%",
          }))
        : result.selections.flatMap((selection) => [
            {
              name: `${BUSBAR_MATERIAL_LABELS[selection.material]} installed area`,
              value: selection.installed_area_mm2,
              unit: "mm²",
            },
            {
              name: `${BUSBAR_MATERIAL_LABELS[selection.material]} utilisation`,
              value: selection.utilisation_percent,
              unit: "%",
            },
          ]);
    case "continuous_current":
      return [
        { name: "Design current", value: result.design_current_a, unit: "A" },
        { name: "Capability", value: result.effective_capacity_a, unit: "A" },
      ];
    case "short_time_withstand":
      return [
        { name: "Ik''", value: result.prospective_current_ka, unit: "kA" },
        { name: "Required duration", value: result.required_fault_duration_s, unit: "s" },
      ];
    case "peak_withstand":
    case "supports":
      return [{ name: "ip", value: result.peak_current_ka, unit: "kA" }];
    case "protective_earth":
      return [{ name: "PE required area", value: result.pe_required_area_mm2, unit: "mm²" }];
    case "voltage_drop":
      return [
        { name: "Drop", value: result.voltage_drop_percent, unit: "%" },
        { name: "Drop", value: result.voltage_drop_v, unit: "V" },
      ];
    case "temperature_rise":
    case "neutral":
    case "clearance":
      return [];
  }
}

function ChecksTable({ result }: { result: BusbarSelectionResponse }) {
  return (
    <Section title="Check detail">
      <table>
        <thead>
          <tr>
            <th scope="col">Check</th>
            <th scope="col">Governing values</th>
            <th scope="col">Result</th>
            <th scope="col">Engine message</th>
          </tr>
        </thead>
        <tbody>
          {result.checks.map((check) => {
            // A check whose figures are all empty shows one dash, not one per figure.
            const held = figuresOf(result, check.key);
            const figures = held.some((figure) => figure.value !== null) ? held : [];
            const resultKey = checkResultKey(check.status);
            return (
              <tr key={check.key} data-check-key={check.key} data-check-status={check.status}>
                <th scope="row">{CHECK_LABELS[check.key]}</th>
                <td>
                  {figures.length === 0
                    ? EMPTY_QUANTITY
                    : figures.map((figure, index) => {
                        const quantity = formatQuantity(figure.value, figure.unit);
                        return (
                          <span key={index} data-figure="true" title={quantity.exact ?? undefined}>
                            {index > 0 ? " · " : null}
                            {figure.name} {quantity.display}
                          </span>
                        );
                      })}
                </td>
                <td data-check-result={resultKey}>
                  {/* The word in a pill: the cell keeps the outcome attribute and the text. */}
                  <span data-check-pill>
                    <StatusIcon tone={checkTone(resultKey)} size={16} />
                    {checkWord(check.key, check.status)}
                  </span>
                </td>
                <td data-check-reason="true">
                  {check.reason}
                  <NotVerified values={check.unverified} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </Section>
  );
}

// The material or materials the study compared: the area the current needs, the arrangement
// that covers it and how full it is. Empty for a busway, which has rows instead.
function SelectionsTable({ result }: { result: BusbarSelectionResponse }) {
  if (result.selections.length === 0) {
    return null;
  }
  return (
    <Section title="Material selections">
      <table>
        <thead>
          <tr>
            <th scope="col">Material</th>
            <th scope="col">Current density</th>
            <th scope="col">Required area</th>
            <th scope="col">Arrangement</th>
            <th scope="col">Installed area</th>
            <th scope="col">Utilisation</th>
          </tr>
        </thead>
        <tbody>
          {result.selections.map((selection) => (
            <tr key={selection.material} data-selection-material={selection.material}>
              <th scope="row">{BUSBAR_MATERIAL_LABELS[selection.material]}</th>
              <Quantity value={selection.current_density_a_mm2} unit="A/mm²" />
              <Quantity value={selection.required_area_mm2} unit="mm²" />
              <td>
                {selection.arrangement === null
                  ? EMPTY_QUANTITY
                  : arrangementText(selection.arrangement)}
              </td>
              <Quantity value={selection.installed_area_mm2} unit="mm²" />
              <Quantity value={selection.utilisation_percent} unit="%" />
            </tr>
          ))}
        </tbody>
      </table>
    </Section>
  );
}

// The catalogue row chosen for each material of a busway study; shown only when there are any.
function BuswayTable({ result }: { result: BusbarSelectionResponse }) {
  if (result.busway_selections.length === 0) {
    return null;
  }
  return (
    <Section title="Busway rows">
      <table>
        <thead>
          <tr>
            <th scope="col">Material</th>
            <th scope="col">Rated current</th>
            <th scope="col">Icw</th>
            <th scope="col">Icw duration</th>
            <th scope="col">Ipk</th>
            <th scope="col">Voltage drop</th>
            <th scope="col">Basis</th>
            <th scope="col">Source</th>
            <th scope="col">Utilisation</th>
          </tr>
        </thead>
        <tbody>
          {result.busway_selections.map((selection) => (
            <tr key={selection.material} data-busway-material={selection.material}>
              <th scope="row">{BUSBAR_MATERIAL_LABELS[selection.material]}</th>
              <Quantity value={selection.row?.rated_current_a ?? null} unit="A" />
              <Quantity value={selection.row?.icw_ka ?? null} unit="kA" />
              <Quantity value={selection.row?.icw_duration_s ?? null} unit="s" />
              <Quantity value={selection.row?.ipk_ka ?? null} unit="kA" />
              <Quantity value={selection.row?.voltage_drop_mv_per_a_m ?? null} unit="mV/A/m" />
              <td>{selection.row?.voltage_drop_basis ?? EMPTY_QUANTITY}</td>
              <td>{selection.row?.source ?? EMPTY_QUANTITY}</td>
              <Quantity value={selection.utilisation_percent} unit="%" />
            </tr>
          ))}
        </tbody>
      </table>
    </Section>
  );
}

function row(key: string, label: string, value: ReactNode, title?: string) {
  return (
    <tr data-row-key={key}>
      <th scope="row">{label}</th>
      <td title={title}>{value}</td>
    </tr>
  );
}

// The fault level the busbar was held against, as the stored fault run holds it - or as typed,
// with no run, case or fault type. ip is empty when it was not held.
function FaultLevelTable({ result }: { result: BusbarSelectionResponse }) {
  const current = formatQuantity(result.prospective_current_ka, "kA");
  const peak = formatQuantity(result.peak_current_ka, "kA");
  const voltage = formatQuantity(result.system_nominal_voltage_v, "V");
  const duration = formatQuantity(result.required_fault_duration_s, "s");

  return (
    <Section title="Fault level">
      <table>
        <thead>
          <tr>
            <th scope="col">Parameter</th>
            <th scope="col">Value</th>
          </tr>
        </thead>
        <tbody>
          {row("current_source", "Fault level source", SOURCE_LABELS[result.current_source])}
          {row("fault_run_id", "Fault run", result.fault_run_id ?? EMPTY_QUANTITY)}
          {row(
            "calculation_case",
            "Calculation case",
            result.calculation_case === null ? EMPTY_QUANTITY : CASE_LABELS[result.calculation_case],
          )}
          {row(
            "fault_type",
            "Fault type",
            result.fault_type === null ? EMPTY_QUANTITY : FAULT_TYPE_LABELS[result.fault_type],
          )}
          {row("fault_bus_code", "Fault bus", result.fault_bus_code)}
          {row("prospective_current_ka", "Prospective current Ik''", current.display, current.exact ?? undefined)}
          {row("peak_current_ka", "Peak current ip", peak.display, peak.exact ?? undefined)}
          {row("system_nominal_voltage_v", "System nominal voltage", voltage.display, voltage.exact ?? undefined)}
          {row("required_fault_duration_s", "Required fault duration", duration.display, duration.exact ?? undefined)}
        </tbody>
      </table>
    </Section>
  );
}

export function BusbarSelectionResultPanel({ result }: BusbarSelectionResultPanelProps) {
  return (
    <article aria-label="Busbar selection result">
      <header>
        <h2>Busbar selection result</h2>
        <p>
          Study code: <strong data-study-code="true">{result.code}</strong>
          {" - "}
          {result.name}
        </p>
        <p>
          Overall status: <strong data-result-status={result.status}>{statusHeading(result)}</strong>
        </p>
      </header>

      <ChecksTable result={result} />
      <SelectionsTable result={result} />
      <BuswayTable result={result} />
      <FaultLevelTable result={result} />

      <footer>
        <p>{DISCLAIMER}</p>
      </footer>
    </article>
  );
}

export default BusbarSelectionResultPanel;
